/**
 * Definições da chamada — o que estava a faltar para uma call "saber usar-se":
 * escolher o microfone, a câmara e o altifalante, e configurar o premir-para-falar.
 *
 * Porque é que isto importa: quase toda a gente tem mais do que um dispositivo
 * (auscultadores + portátil, webcam + câmara interna). Sem este painel a call
 * apanha o default do sistema e reza — e o default quase nunca é o que a pessoa
 * quer. `switchActiveDevice` do LiveKit troca o track sem cair a ligação.
 *
 * Os rótulos dos dispositivos só vêm preenchidos DEPOIS de haver permissão de
 * media; até lá são "" e mostramos um nome por ordem ("Microfone 1"). É por isso
 * que o painel se abre já dentro da call (o track existe) e não no lobby.
 */

import { Fragment, useEffect, useState } from "react";

import type { VoiceActions } from "../hooks/useVoice";
import { keyLabel, type VoicePrefs } from "../lib/voicePrefs";
import { IconClose } from "./Icons";
import { ProvaDoMicrofone } from "./ProvaDoMicrofone";

type Kind = "audioinput" | "videoinput" | "audiooutput";

const ROWS: { kind: Kind; label: string }[] = [
  { kind: "audioinput", label: "Microfone" },
  { kind: "videoinput", label: "Câmera" },
  { kind: "audiooutput", label: "Alto-falante" },
];

type Lists = Record<Kind, MediaDeviceInfo[]>;
type Active = Record<Kind, string | undefined>;

const EMPTY_LISTS: Lists = { audioinput: [], videoinput: [], audiooutput: [] };

/** Nome a mostrar: o do sistema se existir, senão um número por ordem — nunca
 *  uma linha em branco no menu. */
function deviceName(device: MediaDeviceInfo, index: number, label: string): string {
  if (device.label) return device.label;
  return `${label} ${index + 1}`;
}

export function CallSettings({
  actions,
  prefs,
  onPrefs,
  onClose,
}: {
  actions: Pick<VoiceActions, "listDevices" | "setDevice" | "getActiveDevice">;
  prefs: VoicePrefs;
  onPrefs: (prefs: VoicePrefs) => void;
  onClose: () => void;
}) {
  const [lists, setLists] = useState<Lists>(EMPTY_LISTS);
  const [active, setActive] = useState<Active>({
    audioinput: undefined,
    videoinput: undefined,
    audiooutput: undefined,
  });
  const [capturing, setCapturing] = useState(false);

  // Carrega os três tipos ao abrir. `alive` evita escrever estado depois de o
  // painel fechar a meio do await.
  useEffect(() => {
    let alive = true;
    void (async () => {
      const [audioinput, videoinput, audiooutput] = await Promise.all([
        actions.listDevices("audioinput"),
        actions.listDevices("videoinput"),
        actions.listDevices("audiooutput"),
      ]);
      if (!alive) return;
      setLists({ audioinput, videoinput, audiooutput });
      setActive({
        audioinput: actions.getActiveDevice("audioinput"),
        videoinput: actions.getActiveDevice("videoinput"),
        audiooutput: actions.getActiveDevice("audiooutput"),
      });
    })();
    return () => {
      alive = false;
    };
  }, [actions]);

  // Captura de tecla: o próximo toque vira a tecla do premir-para-falar. Escape
  // cancela sem gravar. Um só disparo (`once`) — não fica à escuta pendurado.
  useEffect(() => {
    if (!capturing) return;
    const handler = (event: KeyboardEvent) => {
      event.preventDefault();
      setCapturing(false);
      if (event.code === "Escape") return;
      onPrefs({ ...prefs, pttKey: event.code });
    };
    window.addEventListener("keydown", handler, { once: true });
    return () => window.removeEventListener("keydown", handler);
  }, [capturing, prefs, onPrefs]);

  const change = (kind: Kind, deviceId: string) => {
    setActive((prev) => ({ ...prev, [kind]: deviceId }));
    void actions.setDevice(kind, deviceId);
  };

  return (
    <div className="call-pop call-settings" role="dialog" aria-label="Configurações da chamada">
      <header className="call-pop__head">
        <span>Configurações</span>
        <button type="button" className="call-pop__close" onClick={onClose} title="Fechar">
          <IconClose size={15} />
        </button>
      </header>

      {ROWS.map(({ kind, label }) => {
        const devices = lists[kind];
        return (
          <Fragment key={kind}>
            <label className="call-settings__row" data-kind={kind}>
              <span className="call-settings__label">{label}</span>
              {devices.length ? (
                <select
                  className="call-settings__select"
                  value={active[kind] ?? ""}
                  onChange={(event) => change(kind, event.target.value)}
                >
                  {devices.map((device, index) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {deviceName(device, index, label)}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="call-settings__none">Nenhum disponível</span>
              )}
            </label>
            {/* Debaixo do selector do microfone, e só dele: é ali que se está
              quando se pergunta "porque é que ninguém me ouve?", e a resposta
              tem de estar à vista sem se ir a lado nenhum. */}
            {kind === "audioinput" ? <ProvaDoMicrofone deviceId={active.audioinput} /> : null}
          </Fragment>
        );
      })}

      <div className="call-settings__ptt">
        <label className="call-settings__toggle">
          <input
            type="checkbox"
            checked={prefs.pushToTalk}
            onChange={(event) => onPrefs({ ...prefs, pushToTalk: event.target.checked })}
          />
          <span>Apertar para falar</span>
        </label>
        {prefs.pushToTalk ? (
          <button
            type="button"
            className={`call-settings__key${capturing ? " is-capturing" : ""}`}
            onClick={() => setCapturing(true)}
            title="Clique e aperte a tecla que quer usar"
          >
            {capturing ? "Aperte uma tecla…" : `Tecla: ${keyLabel(prefs.pttKey)}`}
          </button>
        ) : null}
      </div>
    </div>
  );
}
